import { Head, Link } from "@inertiajs/react";
import { Activity, ArrowUpRight, BarChart3, ExternalLink, Headphones, Mic, PlayCircle, TrendingUp } from "lucide-react";

import PublicHeader from "@/components/public-header";
import { SiteFooter } from "@/components/site-footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const HIGHLIGHT_STATS = [
  { label: "Monthly organic visits", value: "5.4K (Ahrefs)", icon: Activity },
  { label: "Estimated social lift", value: "3× CTR", icon: TrendingUp },
  { label: "Annual upside", value: "$50K+", icon: BarChart3 },
];

const OG_IMAGE_EXAMPLES = [
  {
    title: "Show feed takeover",
    description: "Replaces the default “logo only” tile with a vibrant, on-brand overview for every platform.",
    imageUrl: "/case-studies/office-ladies/day-4-office-ladies-after.png",
  },
  {
    title: "Episode art: Mafia",
    description: "Injects the hosts, episode title, and punchy quotes to hook every Office fan at a glance.",
    imageUrl: "/case-studies/office-ladies/day-4-office-ladies-ep-111-mafia-after.png",
  },
  {
    title: "Episode art: Michael Scott Paper Company",
    description: "Showcases the Office nostalgia with branded typography and a bold CTA to listen now.",
    imageUrl: "/case-studies/office-ladies/day-4-office-ladies-ep-99-michael-scott-paper-company.png",
  },
];

const REMAKE_PILLARS = [
  {
    title: "Episode-specific branding",
    description:
      "Each design pulls in episode names, hosts, and signature Office references so fans know exactly what they’ll hear.",
  },
  {
    title: "Campaign-ready variations",
    description:
      "Created platform-specific crops for Apple Podcasts, Spotify, YouTube, and newsletters without extra design lift.",
  },
  {
    title: "CTA-first overlays",
    description:
      "Layered “Listen now” prompts, episode numbers, and sponsor slots to turn every share into measurable downloads.",
  },
];

export default function PodcastNetworkBoostsCaseStudy() {
  return (
    <>
      <Head title="Office Ladies Podcast OG Image Remake Case Study" />

      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-100/40 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
        <div className="container mx-auto max-w-6xl px-4 pt-10 pb-24 sm:px-6 lg:px-8">
          <PublicHeader />

          <article className="mt-16 space-y-16">
            <header className="relative overflow-hidden rounded-3xl bg-white/95 p-10 shadow-xl dark:bg-gray-900/90">
              <div
                className="pointer-events-none absolute inset-0 bg-gradient-to-br from-orange-200/60 via-transparent to-transparent"
                aria-hidden="true"
              />
              <div className="relative grid gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
                <div className="space-y-6">
                  <Badge className="bg-orange-500/10 text-orange-700 dark:bg-orange-500/20 dark:text-orange-200">
                    Case Study · Office Ladies Podcast
                  </Badge>
                  <h1 className="text-4xl font-bold text-gray-900 md:text-5xl dark:text-white">
                    How an OG Pilot Office Ladies remake could deliver 3× more listens and $50K+ in sponsor upside
                  </h1>
                  <p className="text-lg leading-relaxed text-gray-600 dark:text-gray-300">
                    Day #4 of <span className="font-medium">#100DaysOfOGRemakes</span> explored the podcast’s biggest
                    gap: bland “no image available” previews. These OG Pilot prototypes show how every episode could
                    burst with Jenna & Angela’s energy, channel-specific CTAs, and visual hooks that pull fans directly
                    into new episodes.
                  </p>
                  <div className="flex flex-wrap gap-3 text-sm text-gray-500 dark:text-gray-400">
                    <span className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 shadow-sm dark:bg-gray-800">
                      <Mic className="h-4 w-4 text-orange-500" />
                      Published:{" "}
                      <a
                        href="https://x.com/RaulOnRails/status/1976279006048813398"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-orange-700 hover:underline dark:text-orange-300"
                      >
                        Day 4 · #100DaysOfOGRemakes
                      </a>
                    </span>
                    <span className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 shadow-sm dark:bg-gray-800">
                      <Headphones className="h-4 w-4 text-orange-500" />
                      OG Pilot remake: Podcast episode visuals
                    </span>
                  </div>
                </div>

                <div className="grid gap-4 rounded-3xl border border-orange-200/60 bg-orange-100/60 p-6 shadow-inner dark:border-orange-500/30 dark:bg-orange-500/10">
                  {HIGHLIGHT_STATS.map(({ label, value, icon: Icon }) => (
                    <div
                      key={label}
                      className="flex items-start gap-4 rounded-2xl bg-white/90 p-4 shadow dark:bg-gray-950/70"
                    >
                      <div className="rounded-xl bg-orange-500/10 p-3 text-orange-600 dark:bg-orange-500/20 dark:text-orange-200">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold tracking-wide text-gray-500 uppercase dark:text-gray-400">
                          {label}
                        </p>
                        <p className="text-xl font-semibold text-gray-900 dark:text-white">{value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </header>

            <section className="grid gap-10 rounded-3xl bg-white/95 p-10 shadow-xl md:grid-cols-[0.9fr_1.1fr] md:items-center dark:bg-gray-900/90">
              <div className="space-y-5">
                <Badge className="bg-orange-500/10 text-orange-700 dark:bg-orange-500/20 dark:text-orange-200">
                  Baseline snapshot
                </Badge>
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                  The problem: “no image available” previews miss superfans
                </h2>
                <p className="text-base leading-relaxed text-gray-600 dark:text-gray-300">
                  The Office Ladies podcast is a fan magnet—rewatch secrets from Jenna Fischer and Angela Kinsey. But
                  the OG previews? Blank, forgettable placeholders that bury episodes in feeds and kill clicks before
                  they happen.
                </p>
                <ul className="space-y-3 text-sm text-gray-600 dark:text-gray-300">
                  <li>• Default tiles mean no Office nostalgia, no episode hooks, no host energy.</li>
                  <li>• Social shares fail to surface quotes, guests, or iconic moments that make fans smash play.</li>
                  <li>• Sponsors miss out on contextual visibility, keeping CPMs flat.</li>
                </ul>
              </div>
              <Link
                href="https://x.com/RaulOnRails/status/1976279006048813398"
                target="_blank"
                rel="noopener noreferrer"
                className="block transition-transform hover:scale-[1.02] focus-visible:ring-2 focus-visible:ring-orange-400 focus-visible:ring-offset-2 focus-visible:outline-none"
              >
                <blockquote className="rounded-3xl border border-orange-200/70 bg-orange-100/60 p-8 text-lg font-medium text-orange-800 shadow-inner dark:border-orange-500/30 dark:bg-orange-500/10 dark:text-orange-100">
                  <p>
                    “I fired up OG Pilot to remake them: punchy, branded designs with episode vibes, host flair, and
                    that signature Office charm. Now these eye-catchers could drive 3× more clicks straight to listens.”
                  </p>
                  <footer className="mt-4 text-sm text-orange-700/80 dark:text-orange-200/80">
                    — Day #4 · #100DaysOfOGRemakes
                  </footer>
                </blockquote>
              </Link>
            </section>

            <section className="rounded-3xl bg-white/95 p-10 shadow-xl dark:bg-gray-900/90">
              <div className="mb-6 flex flex-wrap items-start justify-between gap-6">
                <div>
                  <Badge className="bg-orange-500/10 text-orange-700 dark:bg-orange-500/20 dark:text-orange-200">
                    OG image gallery
                  </Badge>
                  <h2 className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
                    Download the concept Office Ladies OG Pilot images
                  </h2>
                  <p className="mt-3 max-w-2xl text-base leading-relaxed text-gray-600 dark:text-gray-300">
                    These episode visuals were generated inside OG Pilot as part of this exploratory remake. Plug in
                    your show, mirror this template, and roll it out end-to-end without slowing your release cadence.
                  </p>
                </div>
              </div>

              <div className="grid gap-8 md:grid-cols-3">
                {OG_IMAGE_EXAMPLES.map((item) => (
                  <div
                    key={item.title}
                    className="flex flex-col gap-4 rounded-3xl border border-orange-200/60 bg-white/90 p-4 shadow-sm dark:border-orange-500/30 dark:bg-gray-950/70"
                  >
                    <div className="overflow-hidden rounded-2xl border border-orange-200/40 bg-gray-100 shadow-inner dark:border-orange-500/20 dark:bg-gray-900/80">
                      <img
                        src={item.imageUrl}
                        alt={item.title}
                        className="h-auto w-full rounded-2xl object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{item.title}</h3>
                      <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{item.description}</p>
                    </div>
                    <a
                      href={item.imageUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm font-semibold text-orange-600 transition-colors hover:text-orange-700 dark:text-orange-300 dark:hover:text-orange-200"
                    >
                      Open image <ExternalLink className="h-4 w-4" />
                    </a>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-3xl bg-white/95 p-10 shadow-xl dark:bg-gray-900/90">
              <div className="mb-8 flex flex-wrap items-start justify-between gap-6">
                <div className="max-w-3xl space-y-4">
                  <Badge className="bg-orange-500/10 text-orange-700 dark:bg-orange-500/20 dark:text-orange-200">
                    The OG Pilot remake
                  </Badge>
                  <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                    What could change if OG Pilot took the mic
                  </h2>
                  <p className="text-base leading-relaxed text-gray-600 dark:text-gray-300">
                    OG Pilot can detect a show’s palette, typography, and visual style, then generate episode-ready
                    templates. In this concept, we layered in host photography, episode titles, and social proof so
                    every channel becomes a teaser for the fan-favorite moments inside.
                  </p>
                </div>
                <Button
                  asChild
                  variant="outline"
                  className="border-orange-300 text-orange-600 hover:border-orange-400 hover:bg-orange-50 dark:border-orange-500/40 dark:text-orange-200 dark:hover:border-orange-400 dark:hover:bg-orange-500/10"
                >
                  <Link href="/sign_up">
                    Launch OG images for your podcast
                    <ArrowUpRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>

              <div className="grid gap-6 md:grid-cols-3">
                {REMAKE_PILLARS.map((pillar) => (
                  <div
                    key={pillar.title}
                    className="rounded-2xl border border-orange-200/60 bg-white/90 p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-md dark:border-orange-500/30 dark:bg-gray-950/80"
                  >
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{pillar.title}</h3>
                    <p className="mt-3 text-sm leading-6 text-gray-600 dark:text-gray-300">{pillar.description}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="grid gap-10 rounded-3xl bg-white/95 p-10 shadow-xl lg:grid-cols-2 dark:bg-gray-900/90">
              <div className="space-y-5">
                <Badge className="bg-orange-500/10 text-orange-700 dark:bg-orange-500/20 dark:text-orange-200">
                  Revenue impact modeling
                </Badge>
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                  Modeled path to 2K+ extra listens and $50K annual upside
                </h2>
                <p className="text-base leading-relaxed text-gray-600 dark:text-gray-300">
                  Office Ladies brings in <strong>5.4K organic visits</strong> monthly, worth about $667 in ad value.
                  Social can be a bigger lever. Here’s what our OG Pilot model shows:
                </p>
                <ul className="space-y-3 text-sm text-gray-700 dark:text-gray-200">
                  <li>• Assume 20% of traffic (1.08K visits) currently arrives from social shares.</li>
                  <li>• OG Pilot previews modeled at a 3× CTR → 3.24K social visits per month.</li>
                  <li>
                    • That’s a $400+ monthly traffic value — <strong>$267 more per month</strong> in this projection.
                  </li>
                  <li>
                    • At $25 CPM and full-episode listens, that’s <strong>2K+ modeled extra listens monthly</strong>.
                  </li>
                  <li>
                    • With a 200-episode archive, compounded gains could push past <strong>$50K annually</strong>.
                  </li>
                </ul>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Sponsors notice. Fans binge. OG Pilot can turn every share into a clickable, lovable Office invitation
                  when paired with these industry benchmarks.
                </p>
              </div>
              <div className="flex h-full flex-col justify-center gap-4 rounded-3xl border border-orange-200/60 bg-gradient-to-br from-orange-100 via-white to-orange-50 p-8 shadow-inner dark:border-orange-500/30 dark:from-orange-500/20 dark:via-gray-950 dark:to-orange-500/10">
                <div className="flex items-center gap-3 text-orange-700 dark:text-orange-200">
                  <PlayCircle className="h-5 w-5" />
                  <span className="text-sm font-medium tracking-wide uppercase">Available on every platform</span>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Give supporters the share-worthy art they deserve
                </h3>
                <p className="text-sm leading-6 text-gray-600 dark:text-gray-300">
                  OG Pilot keeps episode drops in sync with Apple Podcasts, Spotify, YouTube, and newsletter embeds.
                  Batch-generate art, trigger crawls, and keep sponsor inventory performing.
                </p>
              </div>
            </section>

            <section className="overflow-hidden rounded-3xl bg-gradient-to-r from-orange-500 via-orange-600 to-rose-500 p-10 text-white shadow-xl">
              <div className="grid gap-10 md:grid-cols-[1.1fr_0.9fr] md:items-center">
                <div className="space-y-4">
                  <h2 className="text-3xl font-bold">Ready to turn your next drop into a sponsored smash hit?</h2>
                  <p className="text-sm leading-6 text-orange-50">
                    OG Pilot automates podcast OG images so your producers, editors, and marketing team can focus on the
                    content. Bring your hosts, quotes, and CTAs into every share automatically.
                  </p>
                  <div className="flex flex-wrap gap-3 text-sm">
                    <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2">
                      <Headphones className="h-4 w-4" />
                      Episode-by-episode automation
                    </span>
                    <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2">
                      <BarChart3 className="h-4 w-4" />
                      Sponsor-ready analytics
                    </span>
                  </div>
                </div>
                <div className="rounded-3xl bg-white/10 p-8 backdrop-blur">
                  <h3 className="text-xl font-semibold">Launch OG Pilot today</h3>
                  <p className="mt-3 text-sm text-orange-50">
                    Start your free trial, import your show, and publish previews that listeners and sponsors can’t
                    ignore.
                  </p>
                  <div className="mt-6 flex flex-wrap gap-3">
                    <Button asChild size="lg" className="bg-white text-orange-600 hover:bg-orange-50">
                      <Link href="/sign_up">Start generating images</Link>
                    </Button>
                    <Button
                      asChild
                      size="lg"
                      variant="outline"
                      className="border-white/60 bg-transparent text-white hover:bg-white/10"
                    >
                      <Link href="/#pricing">View agency plans</Link>
                    </Button>
                  </div>
                  <p className="mt-4 text-xs text-orange-100/90">
                    Framework guides:{" "}
                    <Link href="/nextjs-og-image-generator" className="underline hover:text-white">
                      Next.js
                    </Link>
                    {" · "}
                    <Link href="/rails-og-image-generator" className="underline hover:text-white">
                      Rails
                    </Link>
                    {" · "}
                    <Link href="/astro-og-image-generator" className="underline hover:text-white">
                      Astro
                    </Link>
                  </p>
                </div>
              </div>
            </section>
          </article>
        </div>
      </div>
      <SiteFooter />
    </>
  );
}
